import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import {differenceInYears, parseISO} from 'ag-common/facades'
import actions from 'redux-auto'
import Api from '#services/api'
import {useAzubiList} from '#hooks/store/useAzubiList'
import HorizontalRule from '#components/a/HorizontalRule'
import Modal from '#components/m/Modal'
import Headline from '#components/a/Headline'
import Text from '#components/a/Text'
import Textarea from '#components/a/form/Textarea'
import Input from '#components/a/form/Input'
import DropdownSelect from '#components/m/DropdownSelect'
import Chain from '#components/svg/icons/Chain'
import Checkbox from '#components/a/form/Checkbox'

function NotificationCreateModal({trigger}) {
  const MESSAGE_MAX_LENGTH = 140
  const RECIPIENT_TYPES = [
    {
      value: 'alle',
      label: i18n.t('notification_all_azubis'),
    },
    {
      value: 'regionen',
      label: i18n.t('notification_group_region'),
    },
    {
      value: 'maerkte',
      label: i18n.t('notification_group_market'),
    },
    {
      value: 'berufe',
      label: i18n.t('notification_group_occupation'),
    },
    {
      value: 'ausbildungsjahre',
      label: i18n.t('notification_group_apprentice_year'),
    },
  ]

  const {allAzubis} = useAzubiList()
  const [allReceipientGroups, setAllReceipientGroups] = useState({
    regionen: [],
    maerkte: [],
    berufe: [],
    ausbildungsjahre: [],
  })
  const [recipientGroup, setRecipientGroup] = useState(RECIPIENT_TYPES[0])
  const [subgroups, setSubgroups] = useState()
  const [checkedSubgroups, setCheckedSubgroups] = useState([])
  const [message, setMessage] = useState('')
  const [link, setLink] = useState('')
  const remainingLetters = MESSAGE_MAX_LENGTH - message.length

  function getRecipientGroups() {
    const regionen = []
    const maerkte = []
    const berufe = []
    const ausbildungsjahre = []
    allAzubis.forEach(({region, markt, ausbildungsberuf, ausbildungsBerufString, ausbildungsstart}) => {
      if (!regionen.find(i => region === i.key)) {
        regionen.push({
          key: region,
          value: region,
        })
      }
      if (!maerkte.find(i => markt === i.key)) {
        maerkte.push({
          key: markt,
          value: markt,
        })
      }
      if (!berufe.find(i => ausbildungsberuf === i.key)) {
        berufe.push({
          key: ausbildungsberuf,
          value: ausbildungsBerufString,
        })
      }
      const ausbildungsJahr = differenceInYears(new Date(), parseISO(ausbildungsstart)) + 1
      if (!ausbildungsjahre.find(i => ausbildungsJahr === i.key)) {
        ausbildungsjahre.push({
          key: ausbildungsJahr,
          value: ausbildungsJahr,
        })
      }
    })
    return {
      regionen,
      maerkte,
      berufe,
      ausbildungsjahre,
    }
  }

  useEffect(() => {
    if (allAzubis.length > 0) {
      const recipientGroups = getRecipientGroups()
      setAllReceipientGroups({...recipientGroups})
    }
  }, [allAzubis])

  useEffect(() => {
    if (recipientGroup) {
      setSubgroups(allReceipientGroups[recipientGroup.value])
    }
  }, [recipientGroup, allReceipientGroups])

  const renderCheckList = () => (
    <div className="checkList">
      <div className="section-title">{i18n.t('notification_receipient_select')}</div>
      {subgroups.map(item => (
        <Checkbox
          key={item.key}
          checked={checkedSubgroups.some(x => x === item)}
          onChangeHandle={() => toggleCheck(item)}
          labelText={item.value}
          color="black"
        />
      ))}
    </div>
  )

  const toggleCheck = item => {
    if (checkedSubgroups.indexOf(item) === -1) {
      setCheckedSubgroups([...checkedSubgroups, item])
    } else {
      const newCheckedSubgroups = checkedSubgroups.filter(x => x !== item)
      setCheckedSubgroups(newCheckedSubgroups)
    }
  }

  const resetInput = () => {
    setMessage('')
    setLink('')
    setRecipientGroup(RECIPIENT_TYPES[0])
    setCheckedSubgroups([])
  }

  const getLinkWithProtocol = () => (link && !link.startsWith('http') ? `http://${link}` : link)

  return (
    <Modal
      id={Math.random().toString()}
      modifier="notification"
      trigger={trigger}
      confirmCallback={async () => {
        const linkWithProtocol = getLinkWithProtocol()
        try {
          await Api.createNotificationsForSelectedAzubiGroups(
            message,
            linkWithProtocol,
            recipientGroup.value,
            checkedSubgroups.map(i => i.key),
          )
          resetInput()
        } catch (e) {
          actions.error.writeError({error: e})
        }
      }}
      confirmText={i18n.t('notification_send')}
      hasCloseButton
      confirmButtonDisabled={
        !message || (recipientGroup.value !== RECIPIENT_TYPES[0].value && checkedSubgroups.length === 0)
      }>
      <Headline modifier="article" importance={2} text={i18n.t('notification_send')} />
      <Text text={i18n.t('notification_reminder')} />
      <Text text={i18n.t('remaining_letters_hint', [remainingLetters])} modifier="hint" />
      <Textarea
        testid="message"
        modifier="dark"
        labelText={i18n.t('notification_message')}
        placeholder={i18n.t('notification_placeholder')}
        maxLength={MESSAGE_MAX_LENGTH}
        onChangeHandle={e => setMessage(e.target.value)}
        value={message}
      />
      <div className="link__container">
        <Chain />
        <Input
          testid="link"
          type="url"
          placeholder={i18n.t('notification_add_link')}
          value={link}
          onChangeHandle={e => setLink(e.target.value)}
        />
      </div>

      <HorizontalRule />

      <div className="section-title">{i18n.t('notification_receipient_group')}</div>
      <DropdownSelect
        options={RECIPIENT_TYPES}
        defaultSelect={RECIPIENT_TYPES[0]}
        selectedOption={recipientGroup}
        handleSelect={e => {
          setRecipientGroup(e)
          setCheckedSubgroups([])
        }}
      />

      {subgroups && renderCheckList(subgroups)}
    </Modal>
  )
}

NotificationCreateModal.propTypes = {
  trigger: PropTypes.node,
}

export default NotificationCreateModal
